import React, { useState, memo, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Crown, Coins, Users, Gift, Star, Shield, Trophy, Sparkles, CheckCircle2, Lock, Unlock, MessageCircle, Mail, Copy, ChevronRight, MapPin, Gem, Award, TrendingUp, Calendar, Zap, Bell, Target, X, Check } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
// --- Types ---
interface Reward {
  id: string;
  title: string;
  cost: number;
  unlocked: boolean;
  icon: React.ReactNode;
  description: string;
}
// --- Redemption Modal ---
function RedemptionModal({
  reward,
  onClose,
  onRedeem
}: {
  reward: Reward;
  onClose: () => void;
  onRedeem: (reward: Reward) => void;
}) {
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);
  const handleRedeem = () => {
    setRedeeming(true);
    setTimeout(() => {
      setRedeeming(false);
      setRedeemed(true);
      setTimeout(() => {
        onRedeem(reward);
        onClose();
      }, 1500);
    }, 1500);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-4674" data-mp="1hk99exh3v2p9">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} onClick={e => e.stopPropagation()} className="bg-[#0f0f23] border border-[#d4a017]/30 max-w-md w-full p-8 relative" data-id="element-4675" data-mp="tn3ih73vjfnp">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#f5f0e8]/40 hover:text-[#f5f0e8] transition-colors" data-id="element-4676" data-mp="2qmc4wsj4dnxo">
          <X className="w-5 h-5" data-id="element-4677" data-mp="2hkl15t853r6g" />
        </button>

        {!redeemed ? <>
            <div className="w-20 h-20 mx-auto rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center mb-6 text-[#d4a017]" data-id="element-4678" data-mp="2hmfmh8nbplb2">
              {reward.icon}
            </div>

            <h3 className="font-serif text-3xl text-[#f5f0e8] text-center mb-4" data-id="element-4679" data-mp="3ab35syoii0lz">
              {reward.title}
            </h3>

            <p className="text-[#f5f0e8]/60 text-center mb-8 font-light" data-id="element-4680" data-mp="30ixezs9zxp8d">
              {reward.description}
            </p>

            <div className="bg-[#0a0a0f] border border-[#f5f0e8]/10 p-6 mb-8" data-id="element-4681" data-mp="2vjkrd5bawe4q">
              <div className="flex items-center justify-between mb-4" data-id="element-4682" data-mp="3t018wm1st8sw">
                <span className="text-[#f5f0e8]/60 text-sm" data-id="element-4683" data-mp="3o91cdhwlcsqx">Cost</span>
                <div className="flex items-center gap-2 text-[#d4a017]" data-id="element-4684" data-mp="3o6yfcdx0tzcp">
                  <span className="font-serif text-2xl" data-id="element-4685" data-mp="3vlhe1rskankb">{reward.cost}</span>
                  <Coins className="w-5 h-5" data-id="element-4686" data-mp="4d3aic57q17d" />
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-4687" data-mp="9cg7vqtpmqo7">
                <span className="text-[#f5f0e8]/60 text-sm" data-id="element-4688" data-mp="9f2bjfdhcm15">Your Balance</span>
                <div className="flex items-center gap-2 text-[#f5f0e8]" data-id="element-4689" data-mp="ednc5fwn97q4">
                  <span className="font-serif text-2xl" data-id="element-4690" data-mp="icmin4y0xcjo">275</span>
                  <Coins className="w-5 h-5" data-id="element-4691" data-mp="nch8d562bw35" />
                </div>
              </div>
            </div>

            <button onClick={handleRedeem} disabled={redeeming} className="w-full bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] py-4 text-sm tracking-[0.2em] uppercase font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3" data-id="element-4692" data-mp="s3qxm4np8xei">
              {redeeming ? <>
                  <div className="w-4 h-4 border-2 border-[#0a0a0f]/30 border-t-[#0a0a0f] rounded-full animate-spin" data-id="element-4693" data-mp="23rlb7pe0oyeb"></div>
                  Redeeming...
                </> : <>
                  <Gift className="w-4 h-4" data-id="element-4694" data-mp="2cjdjpujhdxg9" />
                  Redeem Now
                </>}
            </button>
          </> : <div className="text-center py-8" data-id="element-4695" data-mp="28l7hmztl861o">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} className="w-20 h-20 mx-auto rounded-full bg-[#10b981]/20 border border-[#10b981]/40 flex items-center justify-center mb-6" data-id="element-4696" data-mp="1kuafufviap45">
              <Check className="w-10 h-10 text-[#10b981]" data-id="element-4697" data-mp="1ou2th6zrblgd" />
            </motion.div>
            <h3 className="font-serif text-3xl text-[#f5f0e8] mb-4" data-id="element-4698" data-mp="3582ny2fu2iw8">
              Redeemed Successfully!
            </h3>
            <p className="text-[#f5f0e8]/60 font-light" data-id="element-4699" data-mp="309hlrxh0tp55">
              Your {reward.title} will be available at the event.
            </p>
          </div>}
      </motion.div>
    </motion.div>;
}
// --- Invite Modal ---
function InviteModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const inviteLink = 'kingdoms.com/invite/court-275';
  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Join me in the Kingdom Circle! Invite others, earn Kingdom Coins, and unlock exclusive rewards for Life in the Sikh Kingdoms. ${inviteLink}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };
  const handleEmail = () => {
    const subject = encodeURIComponent('Join the Kingdom Circle');
    const body = encodeURIComponent(`I'm inviting you to join the Kingdom Circle!\n\nInvite others, earn Kingdom Coins, and unlock exclusive rewards for Life in the Sikh Kingdoms.\n\nJoin here: ${inviteLink}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-4700" data-mp="nwsgxj7fc4zl">
      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} onClick={e => e.stopPropagation()} className="bg-[#0f0f23] border border-[#d4a017]/30 max-w-lg w-full p-8 relative" data-id="element-4701" data-mp="a059unlrbyfy">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#f5f0e8]/40 hover:text-[#f5f0e8] transition-colors" data-id="element-4702" data-mp="3ieniaqfm1g03">
          <X className="w-5 h-5" data-id="element-4703" data-mp="1xw395a92lisp" />
        </button>

        <div className="w-20 h-20 mx-auto rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center mb-6" data-id="element-4704" data-mp="22vfz43v8n5zi">
          <Users className="w-10 h-10 text-[#d4a017]" data-id="element-4705" data-mp="26ykulmgwi7en" />
        </div>

        <h3 className="font-serif text-3xl text-[#f5f0e8] text-center mb-4" data-id="element-4706" data-mp="2bww41w3aafwc">
          Invite Friends
        </h3>

        <p className="text-[#f5f0e8]/60 text-center mb-8 font-light" data-id="element-4707" data-mp="1f750l7d0liee">
          Share your unique link and earn Kingdom Coins when friends join.
        </p>

        <div className="mb-6" data-id="element-4708" data-mp="1o6s6a5cs1ad9">
          <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-3" data-id="element-4709" data-mp="1o6jtsekkg72l">
            Your Personal Link
          </p>
          <div className="flex items-center bg-[#0a0a0f] border border-[#f5f0e8]/10 p-4" data-id="element-4710" data-mp="1sz32oy8b6e12">
            <span className="text-[#f5f0e8] font-light flex-1 truncate text-sm" data-id="element-4711" data-mp="vhcpt02cep9y">
              {inviteLink}
            </span>
            <button onClick={handleCopy} className="text-[#d4a017] hover:text-[#f5f0e8] transition-colors flex items-center gap-2 text-xs uppercase tracking-wider ml-4" data-id="element-4712" data-mp="10f4ivxeq4hfq">
              {copied ? <CheckCircle2 className="w-4 h-4" data-id="element-4713" data-mp="2v33kxdzwaob" /> : <Copy className="w-4 h-4" data-id="element-4714" data-mp="3uydogxmikyp5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="space-y-3" data-id="element-4715" data-mp="crg4ogbvdqt3">
          <button onClick={handleWhatsApp} className="w-full bg-[#10b981] hover:bg-[#059669] text-white px-8 py-4 text-sm tracking-wider uppercase font-medium transition-colors flex items-center justify-center gap-3" data-id="element-4716" data-mp="cqeh5cdo3buh">
            <MessageCircle className="w-5 h-5" data-id="element-4717" data-mp="hkipo02kzrxo" />
            Share on WhatsApp
          </button>
          <button onClick={handleEmail} className="w-full border border-[#f5f0e8]/20 hover:border-[#f5f0e8]/40 text-[#f5f0e8] px-8 py-4 text-sm tracking-wider uppercase font-light transition-colors flex items-center justify-center gap-3 bg-[#f5f0e8]/5" data-id="element-4718" data-mp="vka8qf7yfyij">
            <Mail className="w-4 h-4" data-id="element-4719" data-mp="1f5ajmw85gu99" />
            Email Invite
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
// --- Hero Section ---
function HeroSection({
  onInviteClick
}: {
  onInviteClick: () => void;
}) {
  const scrollToRewards = () => {
    document.getElementById('rewards')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative w-full min-h-screen bg-[#0a0a0f] flex items-center justify-center overflow-hidden pt-20 pb-32" data-id="element-4720" data-mp="w66dpwbwnglt">
      <div className="absolute inset-0" data-id="element-4721" data-mp="hi1saklgwqzc">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-[#d4a017]/8 rounded-full blur-[150px]" data-id="element-4722" data-mp="hf75wniw8w3a"></div>
        <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png')] opacity-5 bg-cover bg-center mix-blend-overlay" data-id="element-4723" data-mp="8gbzbabxtjzg"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-transparent to-[#0a0a0f]" data-id="element-4724" data-mp="1o85zouotaujc"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto w-full" data-id="element-4725" data-mp="1t8bps6ffo8zb">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-4726" data-mp="1f8sre5cfcc07">
          <Link to="/" className="inline-flex items-center gap-2 text-[#d4a017]/60 hover:text-[#d4a017] text-xs tracking-[0.3em] uppercase font-light mb-16 transition-colors" data-id="element-4727" data-mp="27ysmjch4iapv">
            <ArrowLeft className="w-3 h-3" data-id="element-4728" data-mp="235q33x628hne" />
            Return to the Kingdom
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="mb-16" data-id="element-4729" data-mp="2qur3qy8nioi4">
          <div className="w-24 h-24 mx-auto border-2 border-[#d4a017]/20 rounded-full flex items-center justify-center mb-10 bg-gradient-to-br from-[#d4a017]/10 to-transparent backdrop-blur-sm shadow-[0_0_40px_rgba(212,160,23,0.15)]" data-id="element-4730" data-mp="30zr1x9fp0w0h">
            <Crown className="w-12 h-12 text-[#d4a017]" data-id="element-4731" data-mp="39sue7eelr2tj" />
          </div>
          <h1 className="font-serif text-6xl md:text-8xl text-[#f5f0e8] mb-8 tracking-tight leading-none" data-id="element-4732" data-mp="2m16rl8odidx2">
            Kingdom <span className="text-[#d4a017] italic" data-id="element-4733" data-mp="2i1eohbhr0zz3">Circle</span>
          </h1>
          <p className="text-xl md:text-2xl text-[#f5f0e8]/60 font-light mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-4734" data-mp="2w0nxnr7jettk">
            Invite others. Earn Kingdom Coins. Unlock Treasures. Rise through
            the Court.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20" data-id="element-4735" data-mp="b8ca3b87imh2">
            <button onClick={onInviteClick} className="w-full sm:w-auto bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-10 py-5 text-xs tracking-[0.25em] uppercase font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-[0_0_30px_rgba(212,160,23,0.3)]" data-id="element-4736" data-mp="6a1112pqkfzw">
              <Users className="w-4 h-4" data-id="element-4737" data-mp="na3xsdth6ckq" />
              Invite Friends
            </button>
            <button onClick={scrollToRewards} className="w-full sm:w-auto border border-[#d4a017]/30 hover:border-[#d4a017]/60 text-[#f5f0e8] px-10 py-5 text-xs tracking-[0.25em] uppercase font-light transition-all duration-300 flex items-center justify-center gap-3 bg-[#f5f0e8]/5 backdrop-blur-sm" data-id="element-4738" data-mp="s970vspwa8q2">
              <Trophy className="w-4 h-4" data-id="element-4739" data-mp="9cxfslgkshwk" />
              View My Rewards
            </button>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="max-w-4xl mx-auto" data-id="element-4740" data-mp="381mhq1p58ajb">
          <div className="border border-[#d4a017]/30 bg-gradient-to-br from-[#0f0f23]/95 to-[#1a1a2e]/95 backdrop-blur-xl p-10 md:p-12 relative overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]" data-id="element-4741" data-mp="30jg0ejpp4kbr">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#d4a017]/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" data-id="element-4742" data-mp="2qkgxzivixkx7"></div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10" data-id="element-4743" data-mp="2hsoq9fxvwpwb">
              <div className="text-center" data-id="element-4744" data-mp="2hqkgwih7uyon">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4745" data-mp="2cudgf57lafe3">
                  <Shield className="w-5 h-5 text-[#d4a017]/60" data-id="element-4746" data-mp="27ur36vrrgz5a" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4747" data-mp="27secnu7xtggm">
                    Current Rank
                  </p>
                </div>
                <p className="font-serif text-2xl text-[#d4a017]" data-id="element-4748" data-mp="1yuucyppq3s84">
                  Court Circle
                </p>
              </div>

              <div className="text-center" data-id="element-4749" data-mp="2nmjisdnrh5fv">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4750" data-mp="2si8kamjccwet">
                  <Coins className="w-5 h-5 text-[#d4a017]/60" data-id="element-4751" data-mp="2esevu5br0e5m" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4752" data-mp="2eq2f1vhq89u5">
                    Coin Balance
                  </p>
                </div>
                <p className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4753" data-mp="24sxn1f179k5f">275</p>
              </div>

              <div className="text-center" data-id="element-4754" data-mp="29tlg068zob2a">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4755" data-mp="29qfsgvg0dh8o">
                  <Users className="w-5 h-5 text-[#d4a017]/60" data-id="element-4756" data-mp="1vuu7czcwbhlj" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4757" data-mp="1zvnqlffe8883">
                    Referrals
                  </p>
                </div>
                <p className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4758" data-mp="3u8ngq2hegktm">7</p>
              </div>

              <div className="text-center" data-id="element-4759" data-mp="1ila3epsy6ups">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-4760" data-mp="1imbqxtr5h9oe">
                  <Target className="w-5 h-5 text-[#d4a017]/60" data-id="element-4761" data-mp="1ek8cqxd3jqzf" />
                  <p className="text-[#f5f0e8]/40 text-[10px] tracking-[0.3em] uppercase" data-id="element-4762" data-mp="1ehvok2gqwkdx">
                    Next Unlock
                  </p>
                </div>
                <p className="font-serif text-lg text-[#10b981]" data-id="element-4763" data-mp="1nittqo2x6xy3">
                  Darbar Circle
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Status Snapshot ---
function StatusSnapshot() {
  const stats = [{
    label: 'Current Rank',
    value: 'Court Circle',
    icon: <Shield className="w-6 h-6" data-id="element-4764" data-mp="26acxix4rqg16" />,
    color: 'text-[#d4a017]'
  }, {
    label: 'Kingdom Coins',
    value: '275',
    icon: <Coins className="w-6 h-6" data-id="element-4765" data-mp="gwgee02ayjq9" />,
    color: 'text-[#d4a017]'
  }, {
    label: 'Treasures Collected',
    value: '2 of 5',
    icon: <Gem className="w-6 h-6" data-id="element-4766" data-mp="2bzcskdge24uh" />,
    color: 'text-[#10b981]'
  }, {
    label: 'Successful Referrals',
    value: '7',
    icon: <Users className="w-6 h-6" data-id="element-4767" data-mp="2gyz5smw7vl3a" />,
    color: 'text-[#f5f0e8]'
  }, {
    label: 'Event Access',
    value: 'VIP Eligible',
    icon: <CheckCircle2 className="w-6 h-6" data-id="element-4768" data-mp="2lzn5dwyiha94" />,
    color: 'text-[#10b981]'
  }, {
    label: 'Next Reward',
    value: '25 Coins',
    icon: <Gift className="w-6 h-6" data-id="element-4769" data-mp="2uwxjdq2prdhq" />,
    color: 'text-[#d4a017]'
  }];
  return <section className="py-20 bg-[#05050a] relative -mt-20 z-20" data-id="element-4770" data-mp="2zwiko75cvxxm">
      <div className="container mx-auto px-4 max-w-7xl" data-id="element-4771" data-mp="2zupaihvjavkv">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-4772" data-mp="34p388qxo5esp">
          {stats.map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="border border-[#f5f0e8]/10 bg-[#0f0f23]/80 backdrop-blur-sm p-6 text-center hover:border-[#d4a017]/30 transition-all duration-300 group cursor-pointer" data-id="element-4773" data-mp="39m20f3xrvbo9" data-mpi={i}>
              <div className={`${stat.color} mb-4 flex justify-center opacity-60 group-hover:opacity-100 transition-opacity`} data-id="element-4774" data-mp="t1qawnpm3zai">
                {stat.icon}
              </div>
              <p className="text-[#f5f0e8]/40 text-[9px] tracking-[0.3em] uppercase mb-2" data-id="element-4775" data-mp="1lqdso9b5k6jb">
                {stat.label}
              </p>
              <p className="font-serif text-xl text-[#f5f0e8]" data-id="element-4776" data-mp="1gw14xn2n891r">{stat.value}</p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Rank Progress ---
function RankProgress() {
  return <section className="py-24 bg-[#0a0a0f] border-y border-[#f5f0e8]/5" data-id="element-4777" data-mp="24o840tzr6u4v">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4778" data-mp="24m3tht7cmwvm">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="border border-[#d4a017]/20 bg-gradient-to-br from-[#0f0f23] to-[#1a1a2e] p-12 md:p-16 relative overflow-hidden" data-id="element-4779" data-mp="1czlncnxga0jd">
          <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png')] opacity-5 bg-cover bg-center mix-blend-overlay" data-id="element-4780" data-mp="1vqlgm1bz9wig"></div>

          <div className="relative z-10" data-id="element-4781" data-mp="1mqye1c7iiknt">
            <div className="flex items-center justify-between mb-12 flex-wrap gap-8" data-id="element-4782" data-mp="1ms9i7d4vdufg">
              <div className="flex items-center gap-4" data-id="element-4783" data-mp="1qq7awpxumynl">
                <div className="w-16 h-16 rounded-full border border-[#cd7f32]/40 bg-[#cd7f32]/10 flex items-center justify-center" data-id="element-4784" data-mp="bai6e52798bi">
                  <Shield className="w-8 h-8 text-[#cd7f32]" data-id="element-4785" data-mp="fe6f4r2ki9yc" />
                </div>
                <div data-id="element-4786" data-mp="7s2nncyit2j1">
                  <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-1" data-id="element-4787" data-mp="7r9anu8yxgsg">
                    Current Rank
                  </p>
                  <p className="font-serif text-3xl text-[#f5f0e8]" data-id="element-4788" data-mp="hr05xy5crztl">
                    Court Circle
                  </p>
                </div>
              </div>

              <ChevronRight className="w-8 h-8 text-[#d4a017]/40 hidden md:block" data-id="element-4789" data-mp="cq437nwqmjpr" />

              <div className="flex items-center gap-4" data-id="element-4790" data-mp="3l8ig4c8llix6">
                <div data-id="element-4791" data-mp="3g9fhb9j7o8xn">
                  <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-1 text-right" data-id="element-4792" data-mp="3ga8h1qjr5y6a">
                    Next Rank
                  </p>
                  <p className="font-serif text-3xl text-[#d4a017]" data-id="element-4793" data-mp="3q73fk9g0zykk">
                    Darbar Circle
                  </p>
                </div>
                <div className="w-16 h-16 rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center" data-id="element-4794" data-mp="32g7qvl6v9jhj">
                  <Crown className="w-8 h-8 text-[#d4a017]" data-id="element-4795" data-mp="2xgbjdbyyn9x8" />
                </div>
              </div>
            </div>

            <div className="mb-8" data-id="element-4796" data-mp="el19lpbrq2wj">
              <div className="flex justify-between text-sm mb-3" data-id="element-4797" data-mp="em419zgx09di">
                <span className="text-[#f5f0e8]/60" data-id="element-4798" data-mp="sc7jmfcobixo">
                  Progress to Darbar Circle
                </span>
                <span className="text-[#d4a017] font-medium" data-id="element-4799" data-mp="nlhbiucv51za">
                  7 / 10 referrals
                </span>
              </div>
              <div className="w-full h-2 bg-[#f5f0e8]/10 rounded-full overflow-hidden" data-id="element-4800" data-mp="128spt40fj033">
                <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '70%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 1,
                delay: 0.3
              }} className="h-full bg-gradient-to-r from-[#cd7f32] via-[#d4a017] to-[#d4a017] relative shadow-[0_0_20px_rgba(212,160,23,0.4)]" data-id="element-4801" data-mp="xb0vy4g853w9">
                  <div className="absolute right-0 top-0 bottom-0 w-8 bg-white/20 blur-sm" data-id="element-4802" data-mp="32z9ovd6lm5u1"></div>
                </motion.div>
              </div>
            </div>

            <div className="bg-[#0a0a0f]/60 border border-[#d4a017]/20 p-6" data-id="element-4803" data-mp="2p58wr2a5jh2n">
              <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-3" data-id="element-4804" data-mp="2u4v9lam2ivax">
                Unlock at Darbar Circle
              </p>
              <div className="flex items-start gap-3" data-id="element-4805" data-mp="2k80gx8ch7p4k">
                <Sparkles className="w-5 h-5 text-[#d4a017] mt-0.5 flex-shrink-0" data-id="element-4806" data-mp="2k9jwsxu7zy5t" />
                <p className="text-[#f5f0e8] font-light leading-relaxed" data-id="element-4807" data-mp="269sl53r6etuz">
                  Prototype invitation pool, premium check-in line, exclusive
                  treasure, and signed limited collectible.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Invite Section ---
function InviteSection({
  onInviteClick
}: {
  onInviteClick: () => void;
}) {
  return <section className="py-24 bg-[#05050a]" data-id="element-4808" data-mp="2zyq0etoiff4">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4809" data-mp="2yw4kmf4l56l">
        <div className="text-center mb-16" data-id="element-4810" data-mp="3u68gon15isfn">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-4811" data-mp="3u71jjcwzoxsi">
            <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4812" data-mp="lpx0t2ov2mmg">
              Share the Kingdom
            </p>
            <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4813" data-mp="gy5g38sdzb48">
              Carry the Kingdom Forward
            </h2>
            <p className="text-[#f5f0e8]/50 font-light text-lg max-w-2xl mx-auto" data-id="element-4814" data-mp="bysvkuozmg4t">
              The kingdom returns faster when more people join the circle.
            </p>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="border border-[#d4a017]/20 bg-[#0f0f23] p-10 md:p-12" data-id="element-4815" data-mp="1c0gw0yo8k7wj">
          <button onClick={onInviteClick} className="w-full bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-10 py-6 text-sm tracking-[0.25em] uppercase font-semibold transition-all duration-300 flex items-center justify-center gap-3 mb-10 shadow-[0_0_30px_rgba(212,160,23,0.2)]" data-id="element-4816" data-mp="1qy9b5ppveua7">
            <Users className="w-5 h-5" data-id="element-4817" data-mp="24q5xte8rsbz7" />
            Open Invite Panel
          </button>

          <div className="grid grid-cols-3 gap-6 border-t border-[#f5f0e8]/10 pt-10" data-id="element-4818" data-mp="29oh9luimkwk2">
            <div className="text-center" data-id="element-4819" data-mp="2enu5f4rh5dys">
              <p className="font-serif text-4xl text-[#f5f0e8] mb-2" data-id="element-4820" data-mp="2eovitfvyqcjo">18</p>
              <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4821" data-mp="2ipxke3dcr0ot">
                Invites Sent
              </p>
            </div>
            <div className="text-center border-x border-[#f5f0e8]/10" data-id="element-4822" data-mp="3gbfmtbnsvtj7">
              <p className="font-serif text-4xl text-[#d4a017] mb-2" data-id="element-4823" data-mp="3be5zc1jz1ry0">7</p>
              <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4824" data-mp="3p511kuzytasm">
                Joined
              </p>
            </div>
            <div className="text-center" data-id="element-4825" data-mp="3l6lhayv9qgiw">
              <p className="font-serif text-4xl text-[#f5f0e8] mb-2" data-id="element-4826" data-mp="2xe4opudwn65c">175</p>
              <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4827" data-mp="2sfthlrezv9qt">
                Coins Earned
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Rewards Section ---
function RewardsSection({
  onRewardClick
}: {
  onRewardClick: (reward: Reward) => void;
}) {
  const rewards: Reward[] = [{
    id: '1',
    title: 'Collectible Coin',
    cost: 50,
    unlocked: true,
    icon: <Coins className="w-7 h-7" data-id="element-4828" data-mp="gq0wvvjg9f0k" />,
    description: 'A commemorative Kingdom Circle coin to mark your participation in the movement.'
  }, {
    id: '2',
    title: 'Artifact Pack',
    cost: 100,
    unlocked: true,
    icon: <Gem className="w-7 h-7" data-id="element-4829" data-mp="f9n1ct00pprt" />,
    description: 'A curated collection of digital and physical artifacts from the Sikh Kingdoms.'
  }, {
    id: '3',
    title: 'Premium Drink',
    cost: 75,
    unlocked: true,
    icon: <Gift className="w-7 h-7" data-id="element-4830" data-mp="17ykkeyc1zqlm" />,
    description: 'Complimentary premium beverage at the experience, inspired by royal court traditions.'
  }, {
    id: '4',
    title: 'Priority Line',
    cost: 150,
    unlocked: true,
    icon: <Zap className="w-7 h-7" data-id="element-4831" data-mp="136iy0jl68jhv" />,
    description: 'Skip the general entry line with expedited VIP check-in access.'
  }, {
    id: '5',
    title: 'Upgraded Seat Artifact',
    cost: 200,
    unlocked: true,
    icon: <Star className="w-7 h-7" data-id="element-4832" data-mp="1voy1xux0i71y" />,
    description: 'Enhanced seating with an exclusive artifact placed at your seat.'
  }, {
    id: '6',
    title: 'VIP Access',
    cost: 250,
    unlocked: false,
    icon: <Crown className="w-7 h-7" data-id="element-4833" data-mp="20ifuw8ksey6o" />,
    description: 'Full VIP experience including private lounge access and exclusive content.'
  }, {
    id: '7',
    title: 'Prototype Preview',
    cost: 300,
    unlocked: false,
    icon: <Sparkles className="w-7 h-7" data-id="element-4834" data-mp="1mlj6eds55ssy" />,
    description: 'Invitation to exclusive prototype testing events before the official launch.'
  }];
  return <section id="rewards" className="py-24 bg-[#0a0a0f] border-t border-[#f5f0e8]/5" data-id="element-4835" data-mp="1cueszii46g08">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-4836" data-mp="17w3lvfj7ejlp">
        <div className="text-center mb-16" data-id="element-4837" data-mp="17tig4ffsbjio">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4838" data-mp="13ui014ooyuzd">
            Live Redemption
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4839" data-mp="yw858v6vpwea">
            Redeem at the Experience
          </h2>
          <p className="text-[#f5f0e8]/50 font-light text-lg" data-id="element-4840" data-mp="3ahz6mad2afjy">
            Your Kingdom Coins become real-world value.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-4841" data-mp="3jif7rd6c1aln">
          {rewards.map((reward, i) => <motion.button key={reward.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} onClick={() => reward.unlocked && onRewardClick(reward)} disabled={!reward.unlocked} className={`p-8 text-center border transition-all duration-300 w-full ${reward.unlocked ? 'border-[#d4a017]/30 bg-gradient-to-br from-[#d4a017]/5 to-transparent hover:border-[#d4a017]/50 cursor-pointer hover:scale-105' : 'border-[#f5f0e8]/10 bg-[#0f0f23] opacity-50 cursor-not-allowed'}`} data-id="element-4842" data-mp="3ogywflcb90hp" data-mpi={i}>
              <div className={`w-16 h-16 mx-auto rounded-full border flex items-center justify-center mb-6 ${reward.unlocked ? 'border-[#d4a017]/40 bg-[#d4a017]/10 text-[#d4a017]' : 'border-[#f5f0e8]/20 bg-[#0a0a0f] text-[#f5f0e8]/40'}`} data-id="element-4843" data-mp="11iacs1d23nfw">
                {reward.unlocked ? reward.icon : <Lock className="w-6 h-6" data-id="element-4844" data-mp="1pck6k5m9kpu2" />}
              </div>
              <h4 className="font-serif text-lg text-[#f5f0e8] mb-4" data-id="element-4845" data-mp="1kea8nn95nb4r">
                {reward.title}
              </h4>
              <div className={`inline-flex items-center gap-2 text-sm tracking-wider uppercase ${reward.unlocked ? 'text-[#d4a017]' : 'text-[#f5f0e8]/40'}`} data-id="element-4846" data-mp="1z3ojkzsdq0m9">
                <span data-id="element-4847" data-mp="285o0ls3e94p7">{reward.cost}</span>
                <Coins className="w-3 h-3" data-id="element-4848" data-mp="282jmo304mulc" />
              </div>
            </motion.button>)}
        </div>
      </div>
    </section>;
}
// --- Treasures Section ---
function TreasuresSection() {
  return <section className="py-24 bg-[#05050a]" data-id="element-4849" data-mp="2rqfvw9g30h11">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-4850" data-mp="2rq636e3njvrn">
        <div className="text-center mb-16" data-id="element-4851" data-mp="2dr6nhvy4ar7m">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4852" data-mp="2dqdf6qjiftnd">
            Your Collection
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4853" data-mp="2huctd2hbigdk">
            Kingdom Treasures
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" data-id="element-4854" data-mp="28rk4t6zisirf">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="border border-[#d4a017]/30 bg-gradient-to-br from-[#0f0f23] to-[#1a1a2e] p-10 hover:border-[#d4a017]/50 transition-all duration-300 cursor-pointer group" data-id="element-4855" data-mp="28tn1uaz3bc5n">
            <div className="flex items-center gap-4 mb-8" data-id="element-4856" data-mp="d0cns2mtn10d">
              <div className="w-20 h-20 rounded-full border border-[#d4a017]/40 bg-[#d4a017]/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-4857" data-mp="h0wkj53hg2lz">
                <Gem className="w-10 h-10 text-[#d4a017]" data-id="element-4858" data-mp="m22d92x57pvi" />
              </div>
              <div data-id="element-4859" data-mp="r0nen5o4sflj">
                <div className="inline-block bg-[#d4a017]/10 text-[#d4a017] text-[9px] tracking-widest uppercase px-3 py-1 border border-[#d4a017]/20 mb-2" data-id="element-4860" data-mp="qwzirzv7rh3n">
                  Rare Artifact
                </div>
                <h3 className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4861" data-mp="zxe8dbf7nicy">
                  Royal Seal of Lahore
                </h3>
              </div>
            </div>
            <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-4862" data-mp="19o8xcnjucw23">
              A symbol of authority from the Darbar. Carry this to unlock hidden
              narratives within the experience.
            </p>
            <div className="flex items-center gap-2 text-[#10b981] text-sm" data-id="element-4863" data-mp="1jlnaoeagx4q6">
              <CheckCircle2 className="w-4 h-4" data-id="element-4864" data-mp="347davf976dvx" />
              <span className="uppercase tracking-wider" data-id="element-4865" data-mp="2z6z0hqk4iruq">Collected</span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="border border-[#d4a017]/30 bg-gradient-to-br from-[#0f0f23] to-[#1a1a2e] p-10 hover:border-[#d4a017]/50 transition-all duration-300 cursor-pointer group" data-id="element-4866" data-mp="2u72vpp3lqwe4">
            <div className="flex items-center gap-4 mb-8" data-id="element-4867" data-mp="3d4ni8piw9ivk">
              <div className="w-20 h-20 rounded-full border border-[#10b981]/40 bg-[#10b981]/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-4868" data-mp="3885glyiv20ng">
                <Shield className="w-10 h-10 text-[#10b981]" data-id="element-4869" data-mp="4qx2npr97ky0" />
              </div>
              <div data-id="element-4870" data-mp="4ou7krb8v1mf">
                <div className="inline-block bg-[#10b981]/10 text-[#10b981] text-[9px] tracking-widest uppercase px-3 py-1 border border-[#10b981]/20 mb-2" data-id="element-4871" data-mp="1yxz9vfeovb5c">
                  Uncommon Artifact
                </div>
                <h3 className="font-serif text-2xl text-[#f5f0e8]" data-id="element-4872" data-mp="1p9hdrbhcts81">
                  Akali Warrior's Token
                </h3>
              </div>
            </div>
            <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-4873" data-mp="1g81270eszk28">
              Carried by the Akali Nihangs. A mark of courage and spiritual
              devotion in the face of adversity.
            </p>
            <div className="flex items-center gap-2 text-[#10b981] text-sm" data-id="element-4874" data-mp="16ad6o89di8j0">
              <CheckCircle2 className="w-4 h-4" data-id="element-4875" data-mp="1b8wuke7j1ue0" />
              <span className="uppercase tracking-wider" data-id="element-4876" data-mp="1b9pscpoid9k0">Collected</span>
            </div>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto text-center" data-id="element-4877" data-mp="30onzert39mu2">
          <div className="flex justify-center gap-8 mb-8" data-id="element-4878" data-mp="35n7lcs7omymf">
            <motion.div whileHover={{
            scale: 1.1
          }} className="w-20 h-20 rounded-full border-2 border-[#d4a017] bg-[#d4a017]/10 flex items-center justify-center shadow-[0_0_20px_rgba(212,160,23,0.2)] cursor-pointer" data-id="element-4879" data-mp="35klaaill20zg">
              <Gem className="w-8 h-8 text-[#d4a017]" data-id="element-4880" data-mp="13ri45tqqfdqe" />
            </motion.div>
            <motion.div whileHover={{
            scale: 1.1
          }} className="w-20 h-20 rounded-full border-2 border-[#10b981] bg-[#10b981]/10 flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.2)] cursor-pointer" data-id="element-4881" data-mp="yxdzjh4xvhsh">
              <Shield className="w-8 h-8 text-[#10b981]" data-id="element-4882" data-mp="1mnrhgett809c" />
            </motion.div>
            <div className="w-20 h-20 rounded-full border border-[#f5f0e8]/20 bg-[#0f0f23] flex items-center justify-center opacity-40" data-id="element-4883" data-mp="1hq86powbm5m5">
              <Lock className="w-6 h-6 text-[#f5f0e8]/40" data-id="element-4884" data-mp="3ohqtj2eraln3" />
            </div>
            <div className="w-20 h-20 rounded-full border border-[#f5f0e8]/20 bg-[#0f0f23] flex items-center justify-center opacity-40" data-id="element-4885" data-mp="3ji4dymbggtb4">
              <Lock className="w-6 h-6 text-[#f5f0e8]/40" data-id="element-4886" data-mp="2fipm51p03st8" />
            </div>
            <div className="w-20 h-20 rounded-full border border-[#f5f0e8]/20 bg-[#0f0f23] flex items-center justify-center opacity-40" data-id="element-4887" data-mp="2fgv3ju17ccsb">
              <Lock className="w-6 h-6 text-[#f5f0e8]/40" data-id="element-4888" data-mp="2oj4cwgkqi044" />
            </div>
          </div>

          <div className="mb-4" data-id="element-4889" data-mp="1rtdfa8h5bwu3">
            <div className="flex justify-between text-sm mb-3 text-[#f5f0e8]/60" data-id="element-4890" data-mp="1ru6dum5ybg15">
              <span data-id="element-4891" data-mp="1wreyquihhy63">Collection Progress</span>
              <span className="text-[#d4a017]" data-id="element-4892" data-mp="1wsgdb8ypl2sk">2 of 5</span>
            </div>
            <div className="w-full h-1.5 bg-[#f5f0e8]/10 rounded-full overflow-hidden" data-id="element-4893" data-mp="20t08w83mvycl">
              <motion.div initial={{
              width: 0
            }} whileInView={{
              width: '40%'
            }} viewport={{
              once: true
            }} transition={{
              duration: 1,
              delay: 0.3
            }} className="h-full bg-gradient-to-r from-[#d4a017] to-[#10b981]" data-id="element-4894" data-mp="25ljtvq4nhvrf" />
            </div>
          </div>

          <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-4895" data-mp="wiyse6q69hxw">
            Collect all 5 artifacts to unlock the final{' '}
            <span className="text-[#d4a017]" data-id="element-4896" data-mp="rkngzrk8azdk">Darbar Relic</span>
          </p>
        </div>
      </div>
    </section>;
}
// --- Court Roll ---
function CourtRoll() {
  const leaders = [{
    name: 'Rajveer S.',
    rank: 'Founding Circle',
    referrals: 24,
    badge: 'First Fifty'
  }, {
    name: 'Simran K.',
    rank: 'Darbar Circle',
    referrals: 18,
    badge: 'Court Restorer'
  }, {
    name: 'Harpreet G.',
    rank: 'Darbar Circle',
    referrals: 15,
    badge: 'Five Rivers Collector'
  }, {
    name: 'Amrit P.',
    rank: 'Court Circle',
    referrals: 12,
    badge: 'Prototype Witness'
  }, {
    name: 'Navdeep B.',
    rank: 'Court Circle',
    referrals: 10,
    badge: 'Founding Supporter'
  }];
  return <section className="py-24 bg-[#0a0a0f] border-y border-[#f5f0e8]/5" data-id="element-4897" data-mp="3qb1lvfs1u19y">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-4898" data-mp="3bh5epk7mreob">
        <div className="text-center mb-16" data-id="element-4899" data-mp="3geosyk4bvrg9">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4900" data-mp="3gfgqi0hhjz96">
            The Founders
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4901" data-mp="8183r91na2ox">
            Court Roll
          </h2>
          <p className="text-[#f5f0e8]/50 font-light" data-id="element-4902" data-mp="d02ls8rsfn7z">
            Those who carry the kingdom forward.
          </p>
        </div>

        <div className="space-y-4" data-id="element-4903" data-mp="37wtexxmj3s9">
          {leaders.map((leader, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`border bg-[#0f0f23] p-6 flex items-center justify-between hover:border-[#d4a017]/40 transition-all duration-300 cursor-pointer ${i === 0 ? 'border-[#d4a017]/40 shadow-[0_0_30px_rgba(212,160,23,0.1)]' : 'border-[#f5f0e8]/10'}`} data-id="element-4904" data-mp="qxzlvf5gabre" data-mpi={i}>
              <div className="flex items-center gap-6" data-id="element-4905" data-mp="1xh21k2nr27j3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-serif text-xl ${i === 0 ? 'bg-[#d4a017]/20 text-[#d4a017] border-2 border-[#d4a017]/40' : 'bg-[#f5f0e8]/5 text-[#f5f0e8]/60 border border-[#f5f0e8]/20'}`} data-id="element-4906" data-mp="1shfobt7x8raa">
                  {i + 1}
                </div>
                <div data-id="element-4907" data-mp="3hv2jt9ctesio">
                  <p className="text-[#f5f0e8] font-medium mb-1" data-id="element-4908" data-mp="3huarq8iffcx5">
                    {leader.name}
                  </p>
                  <div className="flex items-center gap-3" data-id="element-4909" data-mp="3dwv3ugq6dv8j">
                    <span className="text-[#f5f0e8]/40 text-xs tracking-wider uppercase" data-id="element-4910" data-mp="1b96fygkwxx71">
                      {leader.rank}
                    </span>
                    <span className="text-[#f5f0e8]/20" data-id="element-4911" data-mp="1l6b2ihio7uod">•</span>
                    <span className="text-[#d4a017] text-xs tracking-wider uppercase" data-id="element-4912" data-mp="1l4q76poj87pg">
                      {leader.badge}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-4913" data-mp="1u0pdw902yl27">
                <p className="font-serif text-2xl text-[#f5f0e8] mb-1" data-id="element-4914" data-mp="1ywmv0hj63vg1">
                  {leader.referrals}
                </p>
                <p className="text-[#f5f0e8]/40 text-[10px] uppercase tracking-widest" data-id="element-4915" data-mp="23x26f3jw2svd">
                  Referrals
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Honors Section ---
function HonorsSection() {
  const honors = [{
    title: 'First Fifty',
    desc: 'Among the first 50 to join',
    earned: true,
    icon: <Star className="w-6 h-6" data-id="element-4916" data-mp="2rt5lo5mlxym6" />
  }, {
    title: 'Five Rivers Collector',
    desc: 'Collected all 5 treasures',
    earned: false,
    icon: <Gem className="w-6 h-6" data-id="element-4917" data-mp="2iyr6s45zoq3l" />
  }, {
    title: 'Court Restorer',
    desc: '10+ successful referrals',
    earned: true,
    icon: <Users className="w-6 h-6" data-id="element-4918" data-mp="2e2af4zzkztlr" />
  }, {
    title: 'Founding Supporter',
    desc: 'Reached Founding Circle',
    earned: false,
    icon: <Crown className="w-6 h-6" data-id="element-4919" data-mp="36olek7bgbogz" />
  }, {
    title: 'Prototype Witness',
    desc: 'Attended prototype event',
    earned: false,
    icon: <Sparkles className="w-6 h-6" data-id="element-4920" data-mp="31s4txn3gns8n" />
  }];
  return <section className="py-24 bg-[#05050a]" data-id="element-4921" data-mp="2wpvxggrdq81s">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4922" data-mp="2wt1mxx3x7by1">
        <div className="text-center mb-16" data-id="element-4923" data-mp="1qbwl53xfrkoj">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4924" data-mp="1q8ikq18c1ljq">
            Recognition
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4925" data-mp="2nn48cu1lul9v">
            Honors & Distinctions
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4926" data-mp="2ipcjk8w9bf9w">
          {honors.map((honor, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`p-8 border transition-all duration-300 cursor-pointer ${honor.earned ? 'border-[#d4a017]/30 bg-gradient-to-br from-[#d4a017]/5 to-transparent hover:border-[#d4a017]/50' : 'border-[#f5f0e8]/10 bg-[#0f0f23] opacity-60 hover:opacity-80'}`} data-id="element-4927" data-mp="2dnn5slhngni4" data-mpi={i}>
              <div className={`w-14 h-14 rounded-full border flex items-center justify-center mb-6 ${honor.earned ? 'border-[#d4a017]/40 bg-[#d4a017]/10 text-[#d4a017]' : 'border-[#f5f0e8]/20 bg-[#0a0a0f] text-[#f5f0e8]/40'}`} data-id="element-4928" data-mp="1gyflthwoimgb">
                {honor.icon}
              </div>
              <h4 className="font-serif text-xl text-[#f5f0e8] mb-3" data-id="element-4929" data-mp="5hbgtrqd1v4p">
                {honor.title}
              </h4>
              <p className="text-[#f5f0e8]/50 text-sm font-light mb-4" data-id="element-4930" data-mp="afcz4afypqf4">
                {honor.desc}
              </p>
              {honor.earned && <div className="flex items-center gap-2 text-[#10b981] text-xs uppercase tracking-wider" data-id="element-4931" data-mp="3dx50gffznmpz">
                  <CheckCircle2 className="w-4 h-4" data-id="element-4932" data-mp="3ivzhpcyb537z" />
                  Earned
                </div>}
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Kingdom Updates ---
function KingdomUpdates() {
  const updates = [{
    type: 'Announcement',
    title: 'Prototype Event Confirmed',
    desc: 'March 2026 in Toronto',
    time: '2 days ago',
    icon: <Bell className="w-5 h-5" data-id="element-4933" data-mp="2n5fq51cfvez8" />
  }, {
    type: 'Milestone',
    title: '500 Members Joined',
    desc: 'The circle grows stronger',
    time: '5 days ago',
    icon: <TrendingUp className="w-5 h-5" data-id="element-4934" data-mp="2wwk1lr2wohpb" />
  }, {
    type: 'Treasure Drop',
    title: 'New Artifact Unlocked',
    desc: "Maharaja's Seal now available",
    time: '1 week ago',
    icon: <Gem className="w-5 h-5" data-id="element-4935" data-mp="2473g7ddpe12l" />
  }, {
    type: 'Event',
    title: 'Trailer Release',
    desc: 'Official experience trailer live',
    time: '2 weeks ago',
    icon: <Calendar className="w-5 h-5" data-id="element-4936" data-mp="2e5j4l8lciw8e" />
  }];
  return <section className="py-24 bg-[#0a0a0f] border-t border-[#f5f0e8]/5" data-id="element-4937" data-mp="2e73zizbkoh6s">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-4938" data-mp="2966gylmnhl4r">
        <div className="text-center mb-16" data-id="element-4939" data-mp="298so4k5mpymg">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4940" data-mp="3pjmx0acaccbc">
            Latest
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4941" data-mp="3kktl2mcnctda">
            From the Court
          </h2>
        </div>

        <div className="space-y-4" data-id="element-4942" data-mp="16fybtfgplt97">
          {updates.map((update, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="border border-[#f5f0e8]/10 bg-[#0f0f23] p-6 hover:border-[#d4a017]/30 transition-colors cursor-pointer" data-id="element-4943" data-mp="1bei03mirzh4q" data-mpi={i}>
              <div className="flex items-start gap-4" data-id="element-4944" data-mp="1ga72dxm6jc4q">
                <div className="w-12 h-12 rounded-full bg-[#d4a017]/10 border border-[#d4a017]/20 flex items-center justify-center text-[#d4a017] flex-shrink-0" data-id="element-4945" data-mp="1la2y79kq3x97">
                  {update.icon}
                </div>
                <div className="flex-1" data-id="element-4946" data-mp="2dsyn7rdv779c">
                  <div className="flex items-center gap-3 mb-2" data-id="element-4947" data-mp="p5kueednsq55">
                    <span className="text-[#d4a017] text-xs tracking-wider uppercase" data-id="element-4948" data-mp="p6w2uri1um2l">
                      {update.type}
                    </span>
                    <span className="text-[#f5f0e8]/20" data-id="element-4949" data-mp="g4eea6ex9hcf">•</span>
                    <span className="text-[#f5f0e8]/40 text-xs" data-id="element-4950" data-mp="g6r5la6kl425">
                      {update.time}
                    </span>
                  </div>
                  <h4 className="text-[#f5f0e8] font-medium text-lg mb-1" data-id="element-4951" data-mp="17v0y1akn4yps">
                    {update.title}
                  </h4>
                  <p className="text-[#f5f0e8]/50 text-sm font-light" data-id="element-4952" data-mp="13vaargubp0q0">
                    {update.desc}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Locations Section ---
function LocationsSection() {
  const locations = [{
    city: 'Toronto',
    members: 142
  }, {
    city: 'Brampton',
    members: 98
  }, {
    city: 'Mississauga',
    members: 76
  }, {
    city: 'Surrey',
    members: 64
  }, {
    city: 'Vancouver',
    members: 52
  }, {
    city: 'Vaughan',
    members: 38
  }, {
    city: 'London',
    members: 24
  }];
  return <section className="py-24 bg-[#05050a]" data-id="element-4953" data-mp="2840ptp1talgr">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-4954" data-mp="28524e3i1dq38">
        <div className="text-center mb-16" data-id="element-4955" data-mp="2d3mzxpmcfllc">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4956" data-mp="2d2u43joxaghz">
            The Circle Expands
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4957" data-mp="2i03pmo96y80j">
            Where the Kingdom Gathers
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-4958" data-mp="2rsrorpnn8o2f">
          {locations.map((location, i) => <motion.div key={i} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.05
        }} className="border border-[#f5f0e8]/10 bg-[#0f0f23] p-6 text-center hover:border-[#d4a017]/30 transition-all duration-300 cursor-pointer hover:scale-105" data-id="element-4959" data-mp="2vv62jvcggxmu" data-mpi={i}>
              <MapPin className="w-6 h-6 text-[#d4a017]/60 mx-auto mb-4" data-id="element-4960" data-mp="1kdwci2c8dl4n" />
              <p className="text-[#f5f0e8] font-medium mb-2" data-id="element-4961" data-mp="1ffl1vpe436ld">{location.city}</p>
              <p className="text-[#f5f0e8]/40 text-sm" data-id="element-4962" data-mp="1fhp6yanqybn8">
                {location.members} members
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Rank System ---
function RankSystemSection() {
  const ranks = [{
    name: 'River Circle',
    threshold: '0–50 Coins',
    benefits: ['General early access', 'Digital treasure', 'Insider updates'],
    color: 'border-[#f5f0e8]/20',
    iconColor: 'text-[#f5f0e8]/60',
    icon: <Shield className="w-8 h-8" data-id="element-4963" data-mp="25rog3jqj1h0x" />
  }, {
    name: 'Court Circle',
    threshold: '50–150 Coins',
    benefits: ['Priority waitlist', 'Collectible upgrade', 'Better seat zone', 'Early merch access'],
    color: 'border-[#cd7f32]/40',
    iconColor: 'text-[#cd7f32]',
    icon: <Award className="w-8 h-8" data-id="element-4964" data-mp="1mzdled610pdw" />
  }, {
    name: 'Darbar Circle',
    threshold: '150–300 Coins',
    benefits: ['Prototype invitation', 'Premium check-in', 'Exclusive treasure', 'Limited collectible'],
    color: 'border-[#c2b280]/60',
    iconColor: 'text-[#c2b280]',
    icon: <Trophy className="w-8 h-8" data-id="element-4965" data-mp="1i0sig5ze3rlr" />
  }, {
    name: 'Founding Circle',
    threshold: '300+ Coins',
    benefits: ['VIP access', 'Hosted preview', 'Premium drink', 'Founder recognition', 'Private events'],
    color: 'border-[#d4a017]',
    iconColor: 'text-[#d4a017]',
    icon: <Crown className="w-8 h-8" data-id="element-4966" data-mp="1v5lqfvds54ud" />,
    glow: true
  }];
  return <section className="py-24 bg-[#0a0a0f] border-y border-[#f5f0e8]/5" data-id="element-4967" data-mp="1v6dkh1rqauij">
      <div className="container mx-auto px-4 max-w-6xl" data-id="element-4968" data-mp="1qdkiglnna0a5">
        <div className="text-center mb-16" data-id="element-4969" data-mp="1lg26emebz4xx">
          <p className="text-[#d4a017]/60 text-xs tracking-[0.3em] uppercase mb-4" data-id="element-4970" data-mp="1lf0vchxbeig7">
            Progression
          </p>
          <h2 className="font-serif text-5xl text-[#f5f0e8] mb-6" data-id="element-4971" data-mp="1hfi7bda75r95">
            The Court Ranks
          </h2>
          <p className="text-[#f5f0e8]/50 font-light text-lg max-w-2xl mx-auto" data-id="element-4972" data-mp="1chgrr2bzca2f">
            Rise through the ranks. Unlock greater access, privileges, and
            recognition.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-4973" data-mp="31u23lplskghh">
          {ranks.map((rank, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`bg-[#0f0f23] border ${rank.color} p-8 relative hover:scale-105 transition-all duration-300 cursor-pointer ${rank.glow ? 'shadow-[0_0_40px_rgba(212,160,23,0.15)]' : ''}`} data-id="element-4974" data-mp="2wv7de2lhvpnc" data-mpi={i}>
              {rank.glow && <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4a017] to-transparent" data-id="element-4975" data-mp="2qfqmk7bvx8jt"></div>}
              <div className={`${rank.iconColor} mb-6`} data-id="element-4976" data-mp="2vg4w5tt4wqjy">{rank.icon}</div>
              <h3 className="font-serif text-2xl text-[#f5f0e8] mb-2" data-id="element-4977" data-mp="30e6g0gydwtwh">
                {rank.name}
              </h3>
              <p className="text-[#f5f0e8]/40 text-xs tracking-widest uppercase mb-8 pb-6 border-b border-[#f5f0e8]/10" data-id="element-4978" data-mp="apxeg1b9jsad">
                {rank.threshold}
              </p>

              <ul className="space-y-3" data-id="element-4979" data-mp="2h32wkdqhpw6w">
                {rank.benefits.map((benefit, j) => <li key={j} className="flex items-start gap-3 text-[#f5f0e8]/70 font-light text-sm" data-id="element-4980" data-mp="2l3mv9lqpp7v5" data-mpi={j}>
                    <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${rank.iconColor}`} data-id="element-4981" data-mp="2cbb2vuc1facj" />
                    {benefit}
                  </li>)}
              </ul>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
// --- Final CTA ---
function FinalCTA({
  onInviteClick
}: {
  onInviteClick: () => void;
}) {
  const scrollToRewards = () => {
    document.getElementById('rewards')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="py-32 bg-[#05050a] relative overflow-hidden border-t border-[#d4a017]/20" data-id="element-4982" data-mp="3isg68ry679o5">
      <div className="absolute inset-0 bg-gradient-to-t from-[#d4a017]/5 to-transparent" data-id="element-4983" data-mp="3nqhoxbrop6z3"></div>
      <div className="absolute inset-0 bg-[url('https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png')] opacity-5 bg-cover bg-center mix-blend-overlay" data-id="element-4984" data-mp="1q268hcadhqt"></div>

      <div className="container mx-auto px-4 relative z-10 text-center max-w-4xl" data-id="element-4985" data-mp="ainkbk4z309b">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-4986" data-mp="5q2uetp6t7b2">
          <Crown className="w-16 h-16 text-[#d4a017] mx-auto mb-10" data-id="element-4987" data-mp="fjbbc5t7bh3j" />
          <h2 className="font-serif text-6xl md:text-7xl text-[#f5f0e8] mb-8 leading-tight" data-id="element-4988" data-mp="tg6n460u7mna">
            Carry the Kingdom Forward
          </h2>
          <p className="text-2xl text-[#f5f0e8]/60 font-light mb-16 max-w-2xl mx-auto" data-id="element-4989" data-mp="2v7thmhjxtzoi">
            Every invitation brings the kingdom closer to life.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-4990" data-mp="2r8b07vrbs6qf">
            <button onClick={onInviteClick} className="w-full sm:w-auto bg-[#d4a017] hover:bg-[#b8890f] text-[#0a0a0f] px-12 py-6 text-sm tracking-[0.25em] uppercase font-semibold transition-all duration-300 shadow-[0_0_30px_rgba(212,160,23,0.3)]" data-id="element-4991" data-mp="u7ewha2eolku">
              Invite More Friends
            </button>
            <button onClick={scrollToRewards} className="w-full sm:w-auto border border-[#f5f0e8]/20 hover:border-[#f5f0e8]/40 text-[#f5f0e8] px-12 py-6 text-sm tracking-[0.25em] uppercase font-light transition-all duration-300 bg-[#f5f0e8]/5 backdrop-blur-sm" data-id="element-4992" data-mp="1d4zhu75yp20p">
              Unlock More Rewards
            </button>
            <Link to="/" className="w-full sm:w-auto border border-[#d4a017]/30 hover:border-[#d4a017]/60 text-[#d4a017] px-12 py-6 text-sm tracking-[0.25em] uppercase font-light transition-all duration-300 bg-[#d4a017]/5 backdrop-blur-sm inline-flex items-center justify-center" data-id="element-4993" data-mp="1rsucht3j5ggf">
              Join the Next Experience
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}
// --- Main Page Component ---
export function KingdomCirclePage() {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const handleRewardClick = (reward: Reward) => {
    setSelectedReward(reward);
  };
  const handleRewardRedeem = (reward: Reward) => {
    console.log('Redeemed:', reward);
    // Here you would typically make an API call to process the redemption
  };
  return <LanguageProvider data-id="element-4994" data-mp="3v8627ay89eo6">
      <div className="relative bg-[#0a0a0f] text-[#f5f0e8] overflow-x-hidden" data-id="element-4995" data-mp="3qbpcwdfaku9i">
        <ScrollProgress data-id="element-4996" data-mp="3q9lay10ye5bv" />
        <Navbar data-id="element-4997" data-mp="rlxn6y4p7fb2" />

        <main className="w-full" data-id="element-4998" data-mp="rmh4qdqfs1ok">
          <HeroSection onInviteClick={() => setShowInviteModal(true)} data-id="element-4999" data-mp="rmqohjkw1clt" />
          <StatusSnapshot data-id="element-5000" data-mp="mtxh0ny1bq61" />
          <RankProgress data-id="element-5001" data-mp="msmd8o4laiex" />
          <InviteSection onInviteClick={() => setShowInviteModal(true)} data-id="element-5002" data-mp="7cphu85sd7ym" />
          <RewardsSection onRewardClick={handleRewardClick} data-id="element-5003" data-mp="7esehb1g1zcb" />
          <TreasuresSection data-id="element-5004" data-mp="bfc8w6umuour" />
          <CourtRoll data-id="element-5005" data-mp="bfm3y8ujbm7b" />
          <HonorsSection data-id="element-5006" data-mp="3tjw1iqdpx4oj" />
          <KingdomUpdates data-id="element-5007" data-mp="3tkfnqjaeif8d" />
          <LocationsSection data-id="element-5008" data-mp="3tl7dvel8bkr9" />
          <RankSystemSection data-id="element-5009" data-mp="3tm0cfsa1b3yb" />
          <FinalCTA onInviteClick={() => setShowInviteModal(true)} data-id="element-5010" data-mp="2d313oqr19l2" />
        </main>

        <Footer data-id="element-5011" data-mp="q372dpggsx8w" />

        {/* Modals */}
        <AnimatePresence data-id="element-5012" data-mp="q48k2crzkhzl">
          {showInviteModal && <InviteModal onClose={() => setShowInviteModal(false)} data-id="element-5013" data-mp="v20k5u2sbaev" />}
          {selectedReward && <RedemptionModal reward={selectedReward} onClose={() => setSelectedReward(null)} onRedeem={handleRewardRedeem} data-id="element-5014" data-mp="gdw0onvwquv1" />}
        </AnimatePresence>
      </div>
    </LanguageProvider>;
}